/*
 * KLUNK campaign attribution helper — prepared for the Shilajit campaign page.
 *
 * Preconditions:
 * - The official Shopify Customer Events / Meta integration is live and has loaded fbq.
 * - This helper is added before the campaign page's custom cart/checkout script.
 * - Call recordAddToCart only after /cart/add.js succeeds.
 * - Call recordInitiateCheckout immediately before redirecting to checkout.
 *
 * This helper intentionally never emits Purchase. Purchase must come from Shopify's
 * server-side Meta integration so it has authoritative value/currency and is not duplicated.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'klunk_meta_campaign_attribution_v1';
  var KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'fbclid'];
  var CART_PREFIX = 'klunk_';

  function safeString(value) {
    return String(value == null ? '' : value).trim().slice(0, 255);
  }

  function readStored() {
    try {
      return JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}') || {};
    } catch (error) {
      return {};
    }
  }

  function currentAttribution() {
    var stored = readStored();
    var params = new URLSearchParams(window.location.search);
    var result = {};

    KEYS.forEach(function (key) {
      var value = safeString(params.get(key) || stored[key]);
      if (value) result[key] = value;
    });

    return result;
  }

  function captureAttribution() {
    var attribution = currentAttribution();
    try {
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attribution));
    } catch (error) {
      // Attribution persistence is best-effort and must never break checkout.
    }
    return attribution;
  }

  async function persistCartAttribution() {
    var attribution = captureAttribution();
    if (!Object.keys(attribution).length) return { saved: false, reason: 'no_campaign_parameters' };

    try {
      var cartResponse = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      var cart = cartResponse.ok ? await cartResponse.json() : {};
      var attributes = Object.assign({}, cart.attributes || {});

      Object.keys(attribution).forEach(function (key) {
        attributes[CART_PREFIX + key] = attribution[key];
      });

      var updateResponse = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attributes })
      });

      return { saved: updateResponse.ok, reason: updateResponse.ok ? null : 'cart_attribute_update_failed' };
    } catch (error) {
      return { saved: false, reason: 'cart_attribute_update_error' };
    }
  }

  function track(eventName, payload) {
    if (typeof window.fbq !== 'function') return false;
    try {
      window.fbq('track', eventName, payload || {});
      return true;
    } catch (error) {
      return false;
    }
  }

  async function recordAddToCart(payload) {
    var persistence = await persistCartAttribution();
    return { attribution: persistence, browserEventSent: track('AddToCart', payload) };
  }

  async function recordInitiateCheckout(payload) {
    var persistence = await persistCartAttribution();
    return { attribution: persistence, browserEventSent: track('InitiateCheckout', payload) };
  }

  window.KlunkMetaTracking = {
    captureAttribution: captureAttribution,
    persistCartAttribution: persistCartAttribution,
    recordAddToCart: recordAddToCart,
    recordInitiateCheckout: recordInitiateCheckout
  };

  captureAttribution();
}());
